import { type ButtonHTMLAttributes, type ReactNode, useId } from 'react';
import { Icon, type IconName, Mark } from './icons';

type Tone = 'gray' | 'green' | 'red' | 'amber' | 'navy' | 'saffron';

export function Badge({ tone = 'gray', children }: { tone?: Tone; children: ReactNode }) {
  return <span className={`badge badge--${tone}`}>{children}</span>;
}

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  small?: boolean;
  inline?: boolean;
  icon?: IconName;
  busy?: boolean;
  busyLabel?: string;
}

export function Button({
  variant = 'primary',
  small,
  inline,
  icon,
  busy,
  busyLabel,
  children,
  className,
  disabled,
  type = 'button',
  ...rest
}: ButtonProps) {
  const classes = [
    'btn',
    variant !== 'primary' && `btn--${variant}`,
    small && 'btn--small',
    inline && 'btn--inline',
    className,
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <button type={type} className={classes} disabled={disabled || busy} aria-busy={busy || undefined} {...rest}>
      {busy ? <span className="spinner" aria-hidden="true" /> : icon ? <Icon name={icon} size={18} /> : null}
      {busy && busyLabel ? busyLabel : children}
    </button>
  );
}

/** A bot message: the EkApp mark beside one or more bubbles. */
export function BotRow({ children }: { children: ReactNode }) {
  return (
    <div className="bot-row">
      <Mark size={28} />
      <div className="bot-row__col">{children}</div>
    </div>
  );
}

export function Bubble({ children }: { children: ReactNode }) {
  return <p className="bubble">{children}</p>;
}

/** A labelled break in the conversation, e.g. "Today". The label is read out; the lines are decoration. */
export function Divider({ children }: { children: ReactNode }) {
  return <p className="divider">{children}</p>;
}

export interface Chip {
  id: string;
  label: string;
  primary?: boolean;
}

export function Chips({ chips, onPick, label }: { chips: Chip[]; onPick: (chip: Chip) => void; label: string }) {
  return (
    <fieldset className="chips">
      <legend className="visually-hidden">{label}</legend>
      {chips.map((chip) => (
        <button
          key={chip.id}
          type="button"
          className={`chip${chip.primary ? ' chip--primary' : ''}`}
          onClick={() => onPick(chip)}
        >
          {chip.label}
        </button>
      ))}
    </fieldset>
  );
}

export function Card({
  icon,
  title,
  subtitle,
  children,
}: {
  icon: IconName;
  title: string;
  subtitle?: string;
  children?: ReactNode;
}) {
  const titleId = useId();
  return (
    <section className="card" aria-labelledby={titleId}>
      <div className="card__head">
        <span className="icon-tile">
          <Icon name={icon} />
        </span>
        <div className="card__titles">
          <h3 className="card__title" id={titleId}>
            {title}
          </h3>
          {subtitle ? <span className="card__subtitle">{subtitle}</span> : null}
        </div>
      </div>
      {children ? <div className="card__body">{children}</div> : null}
    </section>
  );
}

export function Callout({
  tone = 'navy',
  icon = 'info',
  children,
  role,
}: {
  tone?: 'navy' | 'error' | 'amber' | 'green';
  icon?: IconName;
  children: ReactNode;
  role?: 'alert' | 'status';
}) {
  return (
    <div className={`callout${tone === 'navy' ? '' : ` callout--${tone}`}`} role={role}>
      <Icon name={icon} size={18} />
      <div>{children}</div>
    </div>
  );
}

interface FieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: 'text' | 'password' | 'tel' | 'email';
  help?: string;
  error?: string;
  mono?: boolean;
  autoComplete?: string;
  inputMode?: 'text' | 'numeric' | 'tel' | 'email';
  placeholder?: string;
}

export function Field({
  label,
  value,
  onChange,
  type = 'text',
  help,
  error,
  mono,
  autoComplete = 'off',
  inputMode,
  placeholder,
}: FieldProps) {
  const id = useId();
  const helpId = `${id}-help`;
  const message = error ?? help;
  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        className={`input${mono ? ' input--mono' : ''}`}
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        autoComplete={autoComplete}
        inputMode={inputMode}
        placeholder={placeholder}
        spellCheck={false}
        aria-invalid={error ? true : undefined}
        aria-describedby={message ? helpId : undefined}
      />
      {message ? (
        <p id={helpId} className={`field__help${error ? ' field__help--error' : ''}`} aria-live="polite">
          {message}
        </p>
      ) : null}
    </div>
  );
}
